/**
 * Obscurable Navigation
 * @original-idea Kate York
 * @original-idea Oliver Boermans
 * @author Zenobius Jiricek
 *
 * @example
 * see ./header.scss
 */

//
// Utilities
import $ from "jquery";

import debug from "debug";
import plugin from "@core/plugins/lib";
import { selectTarget } from "@core/plugins/lib/selectors";

const PLUGIN_NAME = "NavigationObscurable";
const ELEMENT_SELECTOR = "[data-navigation-obscurable]";
const log = debug("core/components/site-header/navigation--obscurable");

class ObscurableNavigationPlugin {
	constructor(element, options = {}) {
		this.element = element;
		this.$element = $(element);
		this.options = options;
		this.options.tolerancePx = parseInt(this.options.tolerancePx);
		this.options.minExposed = parseInt(this.options.minExposed);

		log("created", { element, options: this.options });
		this.components = {
			obscuredItemContainer: selectTarget(
				this.options.obscuredItemContainerSelector,
				this.$element
			),
			visibleItemContainer: selectTarget(
				this.options.visibleItemContainerSelector,
				this.$element
			),
			additionalItemsContainer: $(this.options.additionalItemsSelector),
			logo: $(this.options.logoSelector),
			outsideAdditionalItemContainerSelector: $(
				this.options.outsideAdditionalItemContainerSelector
			),
			obscuredItemContainerToggle: selectTarget(
				this.options.obscuredItemContainerToggleSelector,
				this.$element
			),
			burgerButton: $(this.options.burgerButtonSelector)
		};

		this.watch();
	}

	watch() {
		log("watch");
		this.currentTestId = 0;
		$(window).resize(() => {
			this.components.visibleItemContainer.addClass("testing");
		});
		$(window).resize(
			this.fn.debounce(
				this.test.bind(this),
				this.options.testTimeoutMs,
				true
			)
		);
		$(window).resize(
			this.fn.debounce(this.test.bind(this), this.options.testTimeoutMs)
		);
		setTimeout(this.test.bind(this), this.options.testTimeoutMs);
	}

	test() {
		log("test");
		this.unObscureItems();
		this.testItems();
	}

	isItemObscured(element) {
		log("isItemObscured", element);
		const $testElement = $(element);
		const width = $testElement.outerWidth(true);
		const rightEdgePosition =
			width + this.options.tolerancePx + this.accumulatedWidth;
		const result = rightEdgePosition > this.actualContainerWidth;
		log(
			"isItemObscured",
			rightEdgePosition,
			this.actualContainerWidth,
			result
		);
		this.accumulatedWidth += width;
		return result;
	}

	unObscureItems() {
		log("unObscureItems");
		// Put everything back in main nav
		// to prepare for testing if they in fact fit.
		selectTarget(
			this.options.intersectingItemSelector,
			this.components.obscuredItemContainer
		)
			.removeClass(this.options.obscuredItemClasses)
			.addClass(this.options.visibleItemClasses)
			.each((index, element) => {
				this.components.visibleItemContainer.append(
					$(element).detach()
				);
			});

		selectTarget(
			this.options.additionalItemSelector,
			this.components.obscuredItemContainer
		)
			.removeClass(this.options.obscuredItemClasses)
			.addClass(this.options.visibleItemClasses)
			.each((index, element) => {
				this.components.additionalItemsContainer.append(
					$(element).detach()
				);
			});

		selectTarget(
			this.options.outsideAdditionalItemSelector,
			this.components.outsideAdditionalItemContainerSelector
		)
			.removeClass(this.options.obscuredItemClasses)
			.addClass(this.options.visibleItemClasses)
			.each((index, element) => {
				this.components.additionalItemsContainer.append(
					$(element).detach()
				);
			});
	}

	moveItem(element) {
		log("moveItem", element);
		const $element = $(element).detach();

		$element
			.addClass(this.options.obscuredItemClasses)
			.removeClass(this.options.visibleItemClasses);

		this.components.obscuredItemContainer.append($element);

		$element.show();
	}

	moveOutsideItem(element) {
		log("moveOutsideItem", element);
		const $element = $(element).detach();

		$element
			.addClass(this.options.obscuredItemClasses)
			.removeClass(this.options.visibleItemClasses);

		this.components.outsideAdditionalItemContainerSelector.append($element);

		$element.show();
	}

	obscureRemaining(items) {
		log('obscureAll');
		// make burger button visible
		if (this.components.burgerButton.length && this.options.showBurgerClass) {
			this.components.burgerButton.addClass(this.options.showBurgerClass);
		}

		// obscure all items
		items.each((index, element) => this.moveItem(element));
	}

	unobscureRemaining() {
		// hide burger button
		if (this.components.burgerButton.length && this.options.showBurgerClass) {
			this.components.burgerButton.removeClass(this.options.showBurgerClass);
		}
	}

	testItems() {
		log("testItems");
		const toggleWidth = this.components.obscuredItemContainerToggle.innerWidth();
		this.components.obscuredItemContainerToggle.detach();

		this.unobscureRemaining();

		// Move the items that don't fit back to mobile nav
		this.components.obscuredItemContainerToggle
			.show()
			.removeClass(this.options.obscuredItemContainerToggleInvisibleClass);
		const $items = selectTarget(
			this.options.intersectingItemSelector,
			this.components.visibleItemContainer
		);
		const $additionalItems = selectTarget(
			this.options.additionalItemSelector,
			this.components.additionalItemsContainer
		);
		const $additionalOutsideItems = selectTarget(
			this.options.outsideAdditionalItemSelector,
			this.components.additionalItemsContainer
		);

		log("testItems.items", $items);

		this.actualContainerWidth = this.components.visibleItemContainer.innerWidth();
		// build a list of items that don't fit
		this.accumulatedWidth = 0;
		let obscuredItems = $items.filter((index, element) =>
			this.isItemObscured(element)
		);
		log("testItems.obscuredItems", obscuredItems);

		// if at least one item doesn't fit
		if (!this.components.visibleItemContainer.is(":visible")) {
			$items.each((index, element) => this.moveItem(element));
		} else if (obscuredItems.length > 0) {
			// reduce width of container
			this.actualContainerWidth = this.actualContainerWidth - toggleWidth;
			log("testItems.atLeatOneObscured", this.actualContainerWidth);

			this.accumulatedWidth = 0;
			obscuredItems = $items
				// re-test visible items based on new container width
				.filter((index, element) => this.isItemObscured(element))
				// then move them
				.each((index, element) => this.moveItem(element));
		}

		// Move additional based on being visible
		if (!this.components.additionalItemsContainer.is(":visible")) {
			$additionalItems.each((index, element) => this.moveItem(element));
			$additionalOutsideItems.each((index, element) =>
				this.moveOutsideItem(element)
			);
		}

		if (obscuredItems.length > 0 || obscuredItems.length == $items.length) {
			this.components.visibleItemContainer.append(
				this.components.obscuredItemContainerToggle
			);
			this.components.logo.removeClass(this.options.logoAdjustmentClass);
		}

		if (!this.components.obscuredItemContainer.children().length > 0) {
			this.components.obscuredItemContainerToggle.hide();
			this.components.logo.addClass(this.options.logoAdjustmentClass);
		}

		if (($items.length - obscuredItems.length) < this.options.minExposed) {
			log('Not enough elements shown - hide them all');
			this.components.obscuredItemContainerToggle.addClass(this.options.obscuredItemContainerToggleInvisibleClass);
			this.obscureRemaining($items);
		} else {
			log('Only a few items hidden, this is okay');
		}

		this.components.visibleItemContainer.removeClass("testing");
	}
}

ObscurableNavigationPlugin.DEFAULTS = {
	tolerancePx: 40,
	testTimeoutMs: 150,
	reflowOnResize: true,
	visibleItemClasses: "navigation__item--unobscured",
	obscuredItemClasses: "navigation__item--obscured",
	visibleItemContainerSelector: "(children) [data-navigation--visible-items]",
	intersectingItemSelector: "(children) [data-navigation--intersecting-item]",
	additionalItemSelector: "(children) [data-navigation--additional-item]",
	obscuredItemContainerSelector:
		"(children) [data-navigation--obscured-items]",
	obscuredItemContainerToggleSelector:
		"(children) [data-navigation--obscured-items-toggle]",
	obscuredItemContainerToggleInvisibleClass: 'navigation__item--invisible',
	additionalItemsSelector: "[data-navigation-additional-items]",
	outsideAdditionalItemSelector:
		"(children) [data-navigation--additional-outside-item]",
	outsideAdditionalItemContainerSelector: "[data-header-controller]",
	logoAdjustmentClass: "navigation__logo-image--overlap-30-years",
	logoSelector: "[data-navigation-logo]",
	minExposed: 0
};

plugin(PLUGIN_NAME, ObscurableNavigationPlugin);

$(document).ready(() => {
	$(ELEMENT_SELECTOR)[PLUGIN_NAME]();
});



// WEBPACK FOOTER //
// ./client/src/core/components/site-header/navigation--obscurable.js